import { getMappedIntent } from "@expert/sales-tools";
import { useMemo } from "react";

import { useAnalytics } from "@/analytics";
import { useResolvedIntent } from "@/customer-info/api";
import { useAgentSdk, useCallbacksRemaining, usePartner } from "@/sdk";
import { useAgentStore } from "@/sdk/agent/store";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { endDynatraceAction, startDynatraceAction } from "@/utils/RealUserMonitoringClient";

import type { CallbackSurface } from "./types";

import { useControlsStore } from "../CallControls/controls.store";
import { clearCallbacksState } from "../eventHandlers";
import { useCallbacksFormStore } from "./callbacksForm.store";

const { setPendingActivity } = useAgentStore.getState();

/**
 * Hook for scheduling callback logic to separate from UI components
 * TODO: This can use more refactoring in the future to separate SDK concerns better than the first pass
 */
export const useScheduleCallback = () => {
    const logger = useLogger({ module: "useScheduleCallback" });
    const partner = usePartner();

    const { canCallback } = useCallbacksRemaining();
    const { intent, intentSource } = useResolvedIntent();
    const agentSDK = useAgentSdk();
    const { dispatcher } = useAnalytics();
    const callbackType = useCallbacksFormStore((state) => state.callbackType);
    const selectedCallbackNowDelay = useCallbacksFormStore((state) => state.selectedCallbackNowDelay);
    const selectedCallbackMdn = useCallbacksFormStore((state) => state.selectedCallbackMdn);
    const selectedCallbackOption = useCallbacksFormStore((state) => state.selectedCallbackOption);

    const setCallbackScheduledSuccess = (callbackSurface: CallbackSurface, callbackReason?: string) => {
        useCallbacksFormStore.getState().setCallbackScheduled(true);

        void dispatcher
            .withMeta({ Client: partner })
            .withExtra({
                isExternal: callbackType === "CallbackLater",
                intent,
                // Mirrors IVRInfoReceived's computation so the two events stay aligned.
                mappedIntent: intent ? getMappedIntent(intent) : undefined,
                intentSource,
                callbackSurface,
                ...(callbackReason ? { callbackReason } : {}),
            })
            .dispatchBusinessEvent("CallbackScheduled");
        useControlsStore.getState().closeCallbackPanel();
    };

    const canScheduleOrUpdateCallback = useMemo(() => {
        if (!callbackType || !selectedCallbackMdn || !canCallback) return false; // if missing info
        if (
            // if valid callback now
            callbackType === "CallbackNow" &&
            (selectedCallbackNowDelay || selectedCallbackOption === "callback-user-now")
        )
            return true;
        return false;
    }, [callbackType, selectedCallbackOption, selectedCallbackMdn, selectedCallbackNowDelay, canCallback]);

    const scheduleCallback = async (callbackSurface: CallbackSurface, callbackReason?: string) => {
        if (callbackType === "CallbackNow") {
            startDynatraceAction("Callback");
            const success = await agentSDK.scheduleCallbackNow(selectedCallbackNowDelay, selectedCallbackMdn);
            endDynatraceAction("Callback", success ? undefined : { error: "CallbackFailed" });
            if (success) setCallbackScheduledSuccess(callbackSurface, callbackReason);
        } else {
            logger.warn({ callbackType }, "Unhandled callback type");
        }
    };

    const cancelScheduledCallback = async () => {
        clearCallbacksState();
        if (agentSDK.serviceProvider === "connect") {
            const currentActivity = agentSDK.getAgentActivity();
            await agentSDK.setAgentActivity(currentActivity);
            setPendingActivity(undefined);
        }
    };

    return {
        scheduleCallback,
        /** whether a callback can currently be submitted or updated */
        canScheduleOrUpdateCallback,
        cancelScheduledCallback,
    };
};
